/**
 * 26 Sep 2026 · Phase 2 — „Benachrichtigungen auf dem Startbildschirm".
 * Fragt die Browser-Erlaubnis NUR nach bewusstem Aktivieren des Schalters ab
 * (nie automatisch). Ohne/mit abgelehnter Erlaubnis läuft die App normal weiter
 * (In-App-Abzeichen aus Phase 1 genügt). Abzeichen-Farbe am App-Symbol gibt das
 * OS vor.
 */
import { useEffect, useState } from "react";
import { BellRing } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

function urlBase64ToUint8Array(value) {
  const pad = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + pad).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

const DEVICE_KEY = "wb.push_device_id";
function deviceId() {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) { id = (crypto.randomUUID && crypto.randomUUID()) || String(Date.now()); localStorage.setItem(DEVICE_KEY, id); }
  return id;
}

export default function PushNotificationsToggle() {
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const supported = typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window;
  const installed = typeof window !== "undefined" && (window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true);

  useEffect(() => {
    if (!supported) return;
    navigator.serviceWorker.ready
      .then((r) => r.pushManager.getSubscription())
      .then((s) => setEnabled(!!s))
      .catch(() => {});
  }, [supported]);

  async function toggle() {
    if (busy) return;
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      if (!enabled) {
        if (Notification.permission === "denied") {
          toast.error("Benachrichtigungen sind im Browser blockiert. Bitte in den Geräteeinstellungen erlauben.");
          return;
        }
        const perm = await Notification.requestPermission();
        if (perm !== "granted") { toast.message("Ohne Erlaubnis erscheint das Abzeichen nur in der App."); return; }
        const { data } = await api.get("/push/public-key");
        if (!data?.public_key) { toast.error("Push ist serverseitig nicht konfiguriert."); return; }
        let sub = await reg.pushManager.getSubscription();
        if (!sub) {
          sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(data.public_key),
          });
        }
        await api.post("/push/subscriptions", { device_id: deviceId(), subscription: sub.toJSON() });
        setEnabled(true);
        toast.success("Benachrichtigungen aktiviert.");
      } else {
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          await api.delete("/push/subscriptions", { data: { endpoint: sub.endpoint } });
          await sub.unsubscribe();
        }
        setEnabled(false);
        toast.success("Benachrichtigungen deaktiviert.");
      }
    } catch (e) {
      toast.error("Aktion fehlgeschlagen. Bitte erneut versuchen.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="push-toggle-block" className="border border-navy/10 bg-white p-5">
      <div className="flex items-center gap-2 mb-1">
        <BellRing className="w-4 h-4 text-gold" strokeWidth={1.6} />
        <div className="label-caps text-gold">Benachrichtigungen auf dem Startbildschirm</div>
      </div>
      <p className="text-[12.5px] text-navy-dark/60 italic leading-relaxed mb-3">
        Erhalten Sie ein Abzeichen am App-Symbol, sobald es Neuigkeiten gibt – auch wenn die App geschlossen ist.
        {!installed && ' Fügen Sie die App dafür zunächst über „Zum Startbildschirm hinzufügen" hinzu.'}
      </p>
      {supported ? (
        <button
          type="button"
          onClick={toggle}
          disabled={busy}
          data-testid="push-toggle-btn"
          aria-pressed={enabled}
          className={`text-[12px] tracking-wide px-4 py-2 border transition-colors ${
            enabled ? "bg-navy text-ivory border-navy" : "bg-white text-navy-dark/85 border-navy/20 hover:border-gold hover:text-navy"
          } ${busy ? "opacity-60 cursor-not-allowed" : ""}`}
        >
          {enabled ? "Aktiviert – zum Deaktivieren tippen" : "Benachrichtigungen aktivieren"}
        </button>
      ) : (
        <div className="text-[12.5px] text-navy-dark/55">Dieses Gerät/Browser unterstützt keine Push-Benachrichtigungen.</div>
      )}
    </div>
  );
}
